import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";

export const useDraggable = (isDraggable: boolean) => {
  const nodeSelectionNodeState = useSelector(
    (state: RootState) => state.yourSlice.nodeSelection.state,
  );

  const MIN_WIDTH_PIXELS = 450;
  const [isDragging, setIsDragging] = useState(false);
  const [leftWidth, setLeftWidth] = useState(50);
  const [rightWidth, setRightWidth] = useState(50);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const leftContainerRef = useRef<HTMLDivElement>(null);
  const rightContainerRef = useRef<HTMLDivElement>(null);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDraggable) {
      setIsDragging(true);
      e.preventDefault();
    }
  };

  const setWidthLimits = (
    widthPercentage: number,
    minWidthPixels: number = MIN_WIDTH_PIXELS,
  ): number => {
    const maxWidthPixels = containerWidth - minWidthPixels; // Maximum width for the left component

    const minWidthPercentage = (minWidthPixels / containerWidth) * 100;
    const maxWidthPercentage = (maxWidthPixels / containerWidth) * 100;

    return Math.min(
      maxWidthPercentage,
      Math.max(minWidthPercentage, widthPercentage),
    );
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isDraggable) return;

      let adjustment = 0;
      if (e.key === "ArrowRight") {
        adjustment = 1;
      } else if (e.key === "ArrowLeft") {
        adjustment = -1;
      }

      setLeftWidth((prevWidth) => {
        const newWidth = prevWidth + adjustment;
        return Math.min(100, Math.max(0, setWidthLimits(newWidth)));
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDraggable, containerWidth],
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const newLeftWidthInPixel = e.clientX - containerRect.left;
        const newRightWidthInPixel = e.clientX - containerRect.right;
        if (
          newLeftWidthInPixel >= MIN_WIDTH_PIXELS &&
          newRightWidthInPixel <= -MIN_WIDTH_PIXELS
        ) {
          const newLeftWidth =
            (newLeftWidthInPixel / containerRect.width) * 100;
          setLeftWidth(
            Math.min(100, Math.max(0, setWidthLimits(newLeftWidth))),
          );
          setRightWidth(100 - newLeftWidth);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDragging, containerWidth],
  );

  const resizeHalfSplitContainersOnSidePaneChange = (
    mainContainer,
    referenceContainer,
    containerToBeResized,
  ) => {
    if (mainContainer.current && referenceContainer.current) {
      const containerRect = mainContainer.current.getBoundingClientRect();
      const referenceContainerRect =
        referenceContainer.current.getBoundingClientRect();
      const totalWidth = containerRect.width;

      let leftWidthPercentage;
      let rightWidthPercentage;

      if (containerToBeResized === "initial") {
        rightWidthPercentage =
          (referenceContainerRect.width / totalWidth) * 100;
        leftWidthPercentage =
          ((totalWidth - referenceContainerRect.width) / totalWidth) * 100;
        setLeftWidth(leftWidthPercentage);
      } else {
        leftWidthPercentage = (referenceContainerRect.width / totalWidth) * 100;
        rightWidthPercentage =
          ((totalWidth - referenceContainerRect.width) / totalWidth) * 100;
      }

      setLeftWidth(leftWidthPercentage);
      setRightWidth(rightWidthPercentage);
    }
  };

  useEffect(() => {
    if (nodeSelectionNodeState === "none") return;
    rightContainerRef.current.clientWidth > leftContainerRef.current.clientWidth
      ? resizeHalfSplitContainersOnSidePaneChange(
          containerRef,
          leftContainerRef,
          "machine",
        )
      : resizeHalfSplitContainersOnSidePaneChange(
          containerRef,
          rightContainerRef,
          "initial",
        );
  }, [nodeSelectionNodeState]);

  const onMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    let resizeTimer: number | undefined;

    const handleResize = () => {
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = window.setTimeout(() => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      }, 250);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (isDragging && isDraggable) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDragging, isDraggable, onMouseMove, onMouseUp]);

  return {
    isDragging,
    leftWidth,
    rightWidth,
    containerRef,
    leftContainerRef,
    rightContainerRef,
    onMouseDown,
    handleKeyDown,
  };
};
